/**
 * Panel manager — registry for panels with show/hide/toggle.
 * Supports exclusive visibility per position (e.g. only one right panel at a time).
 * Wired to appStore.panels and emits panel:toggled events.
 *
 * Session 10: enablement layer (panel picker), pop-out support.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { isTauriRuntime } from '@/utils/tauri';

export interface PanelConfig {
  title: string;
  icon: string;
  position: 'left' | 'right' | 'bottom' | 'overlay';
  /** Pre-built element OR a factory that creates it on first show. */
  element?: HTMLElement;
  /** Lazy factory — called once on first showPanel(). Mutually exclusive with element. */
  factory?: () => HTMLElement;
  defaultVisible: boolean;
}

interface PanelEntry {
  id: string;
  config: PanelConfig;
  /** Whether the wrapper has been appended to the container. */
  mounted: boolean;
  /** Whether the factory has been called and the real content is in the DOM. */
  instantiated: boolean;
  /** Whether this panel is currently popped out to a separate window (desktop). */
  poppedOut: boolean;
}

class PanelManager {
  private panels = new Map<string, PanelEntry>();
  private activeByPosition = new Map<string, string>();
  private previousByPosition = new Map<string, string>();
  /** Panel IDs that are enabled (can be shown). Empty = all enabled. */
  private enabledIds = new Set<string>();

  /** Register a panel. Accepts either a pre-built element or a lazy factory. */
  registerPanel(id: string, config: PanelConfig): void {
    this.panels.set(id, { id, config, mounted: false, instantiated: !!config.element, poppedOut: false });
  }

  // ── Enablement layer (panel picker) ──

  /** Set which panels are enabled. Empty set = all panels enabled (backward compat). */
  setEnabledPanels(ids: Set<string>): void {
    this.enabledIds = new Set(ids);
    // Hide any currently visible panels that are no longer enabled
    for (const [_position, activeId] of this.activeByPosition) {
      if (!this.isEnabled(activeId)) {
        this.hidePanel(activeId);
      }
    }
  }

  /** Check if a panel is enabled. Empty enabledIds = all enabled. */
  isEnabled(id: string): boolean {
    return this.enabledIds.size === 0 || this.enabledIds.has(id);
  }

  /** Get the set of currently enabled panel IDs. */
  getEnabledIds(): Set<string> {
    return new Set(this.enabledIds);
  }

  /** Get all registered panel IDs. */
  getRegisteredIds(): string[] {
    return Array.from(this.panels.keys());
  }

  /** Get panel config by ID. */
  getPanelConfig(id: string): PanelConfig | undefined {
    return this.panels.get(id)?.config;
  }

  // ── Pop-out (desktop multi-window) ──

  /** Check if a panel is currently popped out. */
  isPoppedOut(id: string): boolean {
    return this.panels.get(id)?.poppedOut ?? false;
  }

  /** Mark a panel as popped out (or returned). */
  setPoppedOut(id: string, poppedOut: boolean): void {
    const entry = this.panels.get(id);
    if (entry) {
      entry.poppedOut = poppedOut;
      if (poppedOut) this.hidePanel(id);
    }
  }

  /** Mount all registered panels into their position containers. */
  mountAll(): void {
    // Track which positions have a defaultVisible panel
    const defaultByPosition = new Map<string, string>();

    for (const [id, entry] of this.panels) {
      if (entry.mounted) continue;
      const containerId = this.getContainerId(entry.config.position);
      const container = document.getElementById(containerId);
      if (!container) {
        console.warn(`[Sentinel] Container not found for panel "${id}" at position "${entry.config.position}"`);
        continue;
      }

      if (entry.config.factory && !entry.config.element) {
        // Lazy panel: create an empty placeholder wrapper (no DOM content built yet)
        const wrapper = this.buildLazyWrapper(id, entry.config);
        wrapper.style.display = 'none';
        container.appendChild(wrapper);
      } else {
        // Eager panel: build full wrapper immediately
        const wrapper = this.buildPanelWrapper(id, entry.config);
        wrapper.style.display = 'none';
        container.appendChild(wrapper);
      }
      entry.mounted = true;

      // Track defaultVisible panels per position (first one wins)
      if (entry.config.defaultVisible && !defaultByPosition.has(entry.config.position)) {
        defaultByPosition.set(entry.config.position, id);
      }
    }

    // Show the default panel for each position
    for (const [position, panelId] of defaultByPosition) {
      const entry = this.panels.get(panelId);
      if (!entry) continue;
      const containerId = this.getContainerId(position as 'left' | 'right' | 'bottom');
      const container = document.getElementById(containerId);
      if (!container) continue;

      // Ensure lazy panel is instantiated before showing
      this.ensureInstantiated(panelId);

      // Show this panel wrapper (must restore flex for panel-body layout)
      const wrapper = container.querySelector<HTMLElement>(`[data-panel-id="${panelId}"]`);
      if (wrapper) wrapper.style.display = 'flex';

      // Un-collapse container
      container.classList.remove('collapsed');
      this.activeByPosition.set(position, panelId);
    }

    // Collapse containers with no default panel (skip overlay — it has no collapse styling)
    for (const position of ['left', 'right', 'bottom']) {
      if (!defaultByPosition.has(position)) {
        const containerId = this.getContainerId(position as 'left' | 'right' | 'bottom' | 'overlay');
        const container = document.getElementById(containerId);
        if (container) container.classList.add('collapsed');
      }
    }
  }

  /** Show a panel by ID — hides other panels in the same position. */
  showPanel(id: string): void {
    // Block if panel is disabled or popped out
    if (!this.isEnabled(id)) return;
    if (this.isPoppedOut(id)) return;

    const entry = this.panels.get(id);
    if (!entry) return;

    const containerId = this.getContainerId(entry.config.position);
    const container = document.getElementById(containerId);
    if (!container) return;

    // Record the previous panel so we can restore it on hide
    const currentActive = this.activeByPosition.get(entry.config.position);
    if (currentActive && currentActive !== id) {
      this.previousByPosition.set(entry.config.position, currentActive);
    }

    // Hide all panels in this position
    const wrappers = container.querySelectorAll<HTMLElement>('.panel-inner');
    wrappers.forEach(w => { w.style.display = 'none'; });

    // Lazy instantiation: build the panel content on first show
    this.ensureInstantiated(id);

    // Show target panel (must restore flex for panel-body layout)
    const target = container.querySelector<HTMLElement>(`[data-panel-id="${id}"]`);
    if (target) target.style.display = 'flex';

    // Un-collapse the container (overlay containers don't use collapse)
    if (entry.config.position !== 'overlay') {
      container.classList.remove('collapsed');
    }
    this.activeByPosition.set(entry.config.position, id);

    // Update store
    const currentPanels = appStore.getState().panels;
    if (entry.config.position !== 'overlay') {
      const key = entry.config.position as 'left' | 'right' | 'bottom';
      appStore.setState({
        panels: { ...currentPanels, [key]: true, activePanel: id },
      });
    } else {
      appStore.setState({
        panels: { ...currentPanels, activePanel: id },
      });
    }

    eventBus.emit('panel:toggled', { panelId: id, visible: true });
  }

  /** Hide a panel by ID. Only collapses container if this is the active panel. */
  hidePanel(id: string): void {
    const entry = this.panels.get(id);
    if (!entry) return;

    const containerId = this.getContainerId(entry.config.position);
    const container = document.getElementById(containerId);
    if (!container) return;

    // Hide this panel wrapper
    const wrapper = container.querySelector<HTMLElement>(`[data-panel-id="${id}"]`);
    if (wrapper) wrapper.style.display = 'none';

    // Only handle container state if this was the active panel
    if (this.activeByPosition.get(entry.config.position) === id) {
      this.activeByPosition.delete(entry.config.position);

      // Restore the previous panel for this position if one exists
      const previous = this.previousByPosition.get(entry.config.position);
      this.previousByPosition.delete(entry.config.position);

      if (previous && this.panels.has(previous)) {
        eventBus.emit('panel:toggled', { panelId: id, visible: false });
        this.showPanel(previous);
        return;
      }

      // No previous panel — collapse the container (overlay doesn't collapse)
      if (entry.config.position !== 'overlay') {
        container.classList.add('collapsed');
      }

      // Update store
      const currentPanels = appStore.getState().panels;
      if (entry.config.position !== 'overlay') {
        const key = entry.config.position as 'left' | 'right' | 'bottom';
        appStore.setState({
          panels: { ...currentPanels, [key]: false, activePanel: null },
        });
      } else {
        appStore.setState({
          panels: { ...currentPanels, activePanel: null },
        });
      }
    }

    eventBus.emit('panel:toggled', { panelId: id, visible: false });
  }

  /** Toggle a panel by ID. */
  togglePanel(id: string): void {
    if (!this.isEnabled(id)) return;

    const entry = this.panels.get(id);
    if (!entry) return;

    const containerId = this.getContainerId(entry.config.position);
    const container = document.getElementById(containerId);
    if (!container) return;

    const isContainerVisible = !container.classList.contains('collapsed');
    const isActivePanel = this.activeByPosition.get(entry.config.position) === id;

    if (isContainerVisible && isActivePanel) {
      // Already showing this panel — collapse the container
      this.hidePanel(id);
    } else {
      // Either container is collapsed or a different panel is active — switch to this panel
      this.showPanel(id);
    }
  }

  /** Hide the currently active panel in a given position. */
  hideActivePanel(position: 'left' | 'right' | 'bottom' | 'overlay'): void {
    const activeId = this.activeByPosition.get(position);
    if (activeId) {
      this.hidePanel(activeId);
    }
  }

  /** Get a registered panel's element. Instantiates lazy panels if needed. */
  getPanelElement(id: string): HTMLElement | undefined {
    this.ensureInstantiated(id);
    return this.panels.get(id)?.config.element;
  }

  /**
   * Ensure a lazy panel's factory has been called and content is in the DOM.
   * No-op for eager panels or already-instantiated panels.
   */
  private ensureInstantiated(id: string): void {
    const entry = this.panels.get(id);
    if (!entry || entry.instantiated) return;

    const factory = entry.config.factory;
    if (!factory) return;

    // Call the factory to build the panel element
    const element = factory();
    entry.config.element = element;
    entry.instantiated = true;

    // Find the placeholder wrapper and populate it
    const containerId = this.getContainerId(entry.config.position);
    const container = document.getElementById(containerId);
    if (!container) return;

    const wrapper = container.querySelector<HTMLElement>(`[data-panel-id="${id}"]`);
    if (!wrapper) return;

    // The lazy wrapper only has header + empty body. Populate the body.
    const body = wrapper.querySelector('.panel-body');
    if (body) {
      body.appendChild(element);
    }
  }

  /** Build a lightweight wrapper with header but no content (for lazy panels). */
  private buildLazyWrapper(id: string, config: PanelConfig): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'panel-inner';
    wrapper.dataset.panelId = id;
    const height = config.position === 'overlay' ? 'auto' : '100%';
    wrapper.style.cssText = `display: flex; flex-direction: column; height: ${height}; width: 100%;`;

    wrapper.appendChild(this.buildPanelHeader(id, config));

    // Empty body — will be populated by ensureInstantiated()
    const body = document.createElement('div');
    body.className = 'panel-body';
    wrapper.appendChild(body);

    return wrapper;
  }

  private buildPanelWrapper(id: string, config: PanelConfig): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'panel-inner';
    wrapper.dataset.panelId = id;
    const height = config.position === 'overlay' ? 'auto' : '100%';
    wrapper.style.cssText = `display: flex; flex-direction: column; height: ${height}; width: 100%;`;

    wrapper.appendChild(this.buildPanelHeader(id, config));

    // Body
    const body = document.createElement('div');
    body.className = 'panel-body';
    if (config.element) body.appendChild(config.element);
    wrapper.appendChild(body);

    return wrapper;
  }

  /** Shared panel header: title + pop-out button (desktop) + close button. */
  private buildPanelHeader(id: string, config: PanelConfig): HTMLElement {
    const header = document.createElement('div');
    header.className = 'panel-header';

    const title = document.createElement('span');
    title.className = 'panel-title';
    title.textContent = config.title;
    header.appendChild(title);

    const btnGroup = document.createElement('div');
    btnGroup.className = 'panel-header-btns';

    // Pop-out button (desktop only — Tauri detected at runtime)
    if (isTauriRuntime()) {
      const popoutBtn = document.createElement('button');
      popoutBtn.className = 'panel-popout-btn';
      popoutBtn.textContent = '\u2197';
      popoutBtn.title = `Pop out ${config.title}`;
      popoutBtn.setAttribute('aria-label', `Pop out ${config.title}`);
      popoutBtn.onclick = (e) => {
        e.stopPropagation();
        eventBus.emit('panel:toggled', { panelId: `popout:${id}`, visible: true });
      };
      btnGroup.appendChild(popoutBtn);
    }

    const closeBtn = document.createElement('button');
    closeBtn.className = 'panel-close-btn';
    closeBtn.textContent = '\u00D7';
    closeBtn.title = `Close ${config.title}`;
    closeBtn.setAttribute('aria-label', `Close ${config.title}`);
    closeBtn.onclick = () => this.hidePanel(id);
    btnGroup.appendChild(closeBtn);

    header.appendChild(btnGroup);
    return header;
  }

  private getContainerId(position: 'left' | 'right' | 'bottom' | 'overlay'): string {
    switch (position) {
      case 'left': return 'panel-left';
      case 'right': return 'panel-right';
      case 'bottom': return 'panel-bottom';
      case 'overlay': return 'overlay-right';
    }
  }
}

/** Singleton panel manager. */
export const panelManager = new PanelManager();
